import { Injectable } from '@angular/core';
import { DataService, NotificationService } from '@vendure/admin-ui/core';
import { Subject, firstValueFrom } from 'rxjs';

import {
  CREATE_REDIRECT_MUTATION,
  DELETE_REDIRECTS_MUTATION,
  EXPORT_REDIRECTS_QUERY,
  IMPORT_REDIRECTS_MUTATION,
  type CreateRedirectResult,
  type CreateRedirectResultData,
  type CreateRedirectVars,
  type DeleteRedirectsResult,
  type DeleteRedirectsResultData,
  type ExportRedirectsResultData,
  type ImportRedirectsResult,
  type ImportRedirectsResultData,
} from './redirects-admin-api';

@Injectable({ providedIn: 'root' })
export class RedirectsDataService {
  private refreshRedirectList = new Subject<void>();
  refreshRedirectList$ = this.refreshRedirectList.asObservable();

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService,
  ) {}

  async deleteRedirects(
    redirectIds: string[],
  ): Promise<DeleteRedirectsResultData> {
    const result = await firstValueFrom(
      this.dataService.mutate<DeleteRedirectsResult>(
        DELETE_REDIRECTS_MUTATION,
        { deleteRedirectsInput: { ids: redirectIds } },
      ),
    );

    this.refreshRedirectList.next();
    return result.deleteRedirects;
  }

  async importRedirects(file: File): Promise<ImportRedirectsResultData> {
    const result = await firstValueFrom(
      this.dataService.mutate<ImportRedirectsResult>(
        IMPORT_REDIRECTS_MUTATION,
        {
          file,
        },
      ),
    );
    this.refreshRedirectList.next();

    return result.importRedirects;
  }

  async createRedirect(
    input: CreateRedirectVars['createRedirectInput'],
  ): Promise<CreateRedirectResultData> {
    const result = await firstValueFrom(
      this.dataService.mutate<CreateRedirectResult>(CREATE_REDIRECT_MUTATION, {
        createRedirectInput: { ...input },
      }),
    );
    this.refreshRedirectList.next();
    const resultData = result?.createRedirect;
    if (resultData.error) {
      this.notificationService.error(
        `Failed to create redirect: ${resultData.error}`,
      );
    } else if (resultData.succeeded) {
      this.notificationService.success('Redirect created successfully');
    }

    return resultData;
  }

  async exportRedirects(): Promise<ExportRedirectsResultData['csvContent']> {
    return await firstValueFrom(
      this.dataService
        .query(EXPORT_REDIRECTS_QUERY)
        .mapSingle((r) => r.exportRedirects.csvContent),
    );
  }
}
